import React from "react";
import { formatNumber } from "../../utils/formatter";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { executePerps } from "../../functions/Contract";
import { useState } from "react";
import UpdateActionsModal from "../Modals/UpdateActionsModal";
import { useWalletConnection } from "../../../hooks/useWalletConnection";
import { cancelLimitOrderEvm } from "../../../evm/services/tradingEvmService";
import { getMarketName } from "../../../utils/marketUtils";
import { getMarketByMarketIndex } from "../../../config/markets";
import { getNibiruSDK, getContractAddresses } from "../../../config/network";
import { formatPriceWithSignificantDecimals } from "../../../Components/utils/formatter";
import { GreenButton } from "../../../Components/Buttons/GreenButton";
import { RedButton } from "../../../Components/Buttons/RedButton";

const nibiru = getNibiruSDK();
const formatDate = (timestamp) => {
  try {
    console.log("Original timestamp:", timestamp, "Type:", typeof timestamp);
    
    if (!timestamp) {
      return { date: "Recent", time: "" };
    }

    let date;
    
    // Handle different timestamp formats
    if (typeof timestamp === 'string') {
      // Remove any extra characters and parse
      const cleanTimestamp = timestamp.trim();
      
      if (cleanTimestamp.includes('T') || cleanTimestamp.includes('-')) {
        // ISO string format
        date = new Date(cleanTimestamp);
      } else {
        // Numeric string - try parsing as number
        const numTimestamp = parseInt(cleanTimestamp);
        if (!isNaN(numTimestamp)) {
          // Handle nanosecond timestamps (19 digits) - convert to milliseconds
          if (numTimestamp.toString().length >= 19) {
            // Nanoseconds - divide by 1,000,000 to get milliseconds
            date = new Date(Math.floor(numTimestamp / 1000000));
          } else if (numTimestamp > 1e12) {
            // Already in milliseconds
            date = new Date(numTimestamp);
          } else {
            // Seconds - multiply by 1000 to get milliseconds
            date = new Date(numTimestamp * 1000);
          }
        }
      }
    } else if (typeof timestamp === 'number') {
      // Handle nanosecond timestamps (19 digits) - convert to milliseconds
      if (timestamp.toString().length >= 19) {
        // Nanoseconds - divide by 1,000,000 to get milliseconds
        date = new Date(Math.floor(timestamp / 1000000));
      } else if (timestamp > 1e12) {
        // Already in milliseconds
        date = new Date(timestamp);
      } else {
        // Seconds - multiply by 1000 to get milliseconds
        date = new Date(timestamp * 1000);
      }
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      // Try to convert to string first
      date = new Date(String(timestamp));
    }

    console.log("Parsed date:", date);

    // Validate the date
    if (!date || isNaN(date.getTime())) {
      console.error("Invalid date created from timestamp:", timestamp);
      return { date: "Recent", time: "" };
    }

    // Format the date and time
    const dateStr = date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });

    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    console.log("Formatted result:", { date: dateStr, time: timeStr });
    return { date: dateStr, time: timeStr };

  } catch (error) {
    console.error("Error formatting date:", error, "Original timestamp:", timestamp);
    return { date: "Recent", time: "" };
  }
};

const OrdersRow = ({ trade, onTradeClick }) => {
  const { account, walletType, isEvm, isCosmos } = useWalletConnection();
  const dispatch = useDispatch();
  const [modalShow, setModalShow] = useState(false);

  const lowPriceTokenIds = new Set(["pump", "pengu", "bonk", "shib", "nibi"]);

  // Only show open conditional orders (limit or stop).
  if ((trade.trade_type !== "limit" && trade.trade_type !== "stop") || !trade.is_open) {
    return null;
  }

  const getTradeIndexNumber = (userTradeIndex) => {
    if (!userTradeIndex) return "N/A";
    if (typeof userTradeIndex === "string" && userTradeIndex.includes("UserTradeIndex")) {
      const match = userTradeIndex.match(/UserTradeIndex\((\d+)\)/);
      return match ? match[1] : userTradeIndex;
    }
    const match = userTradeIndex.toString().match(/\((\d+)\)/);
    return match ? match[1] : userTradeIndex;
  };

  // Updated format collateral amount function to return separate amount and currency
  const formatCollateralAmount = (amount, denom = "USDC") => {
    try {
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount)) return { amount: "$0.00", currency: denom };
      
      const formattedAmount = (numAmount / 1000000).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      return { amount: `$${formattedAmount}`, currency: denom };
    } catch (error) {
      console.error("Error formatting collateral amount:", error);
      return { amount: "$0.00", currency: denom };
    }
  };

  // Updated format size function to return separate amount and currency
  const formatSizeAmount = (amount, leverage, denom = "USDC") => {
    try {
      const numAmount = parseFloat(amount);
      const numLeverage = parseFloat(leverage);
      
      if (isNaN(numAmount) || isNaN(numLeverage)) return { amount: "$0.00", currency: denom };
      
      const totalSize = (numAmount * numLeverage) / 1000000;
      const formattedSize = totalSize.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      return { amount: `$${formattedSize}`, currency: denom };
    } catch (error) {
      console.error("Error formatting size amount:", error);
      return { amount: "$0.00", currency: denom };
    }
  };

  const handleCancelLimitOrder = async () => {
    try {
      if (!account) {
        toast.error("Please connect your wallet");
        return;
      }

      // ✅ Add validation for trade type
      if (trade.trade_type !== "limit") {
        toast.error(`Cannot cancel ${trade.trade_type} order. Only limit orders can be cancelled.`);
        return;
      }

      if (!trade.is_open) {
        toast.error("Cannot cancel a closed order");
        return;
      }

      console.log("🔍 Cancelling limit order:", {
        tradeType: trade.trade_type,
        tradeIndex: trade.user_trade_index,
        isOpen: trade.is_open,
        walletType,
        isEvm,
        isCosmos
      });

      if (isEvm) {
        const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);
        
        const result = await cancelLimitOrderEvm(tradeIndexNumber, dispatch);
        
        if (!result.success) {
          throw new Error(result.error || "EVM cancel limit order failed");
        }
        
        toast.success("Limit order cancelled successfully!");
        
        // SMART EVENT: Emit immediate user action
        window.dispatchEvent(new CustomEvent('userTradeAction', {
          detail: { 
            action: 'cancel_limit_order',
            tradeIndex: trade.user_trade_index,
            timestamp: Date.now()
          }
        }));
        
        // DELAYED CHECK: Verify state change after blockchain confirmation
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('blockchainStateCheck', {
            detail: { account, timestamp: Date.now() }
          }));
        }, 3000);
        
        return result;
      } else {
        if (!walletType && !isCosmos) {
          console.error("Invalid wallet state:", { account, walletType, isEvm, isCosmos });
          toast.error("Wallet connection issue. Please reconnect your wallet.");
          return;
        }

        const message = {
          close_trade: {
            trade_index: trade.user_trade_index,
          },
        };

        console.log("Sending cancel message:", message);

        const res = await executePerps(
          nibiru.chainId,
          nibiru.endptTm,
          getContractAddresses().perp,
          dispatch,
          message,
          account,
          walletType
        );

        if (!res) {
          throw new Error("Transaction failed");
        }

        toast.success("Limit order cancelled successfully!");
        
        // SMART EVENT: Emit immediate user action
        window.dispatchEvent(new CustomEvent('userTradeAction', {
          detail: { 
            action: 'cancel_limit_order',
            tradeIndex: trade.user_trade_index,
            timestamp: Date.now()
          }
        }));
        
        // DELAYED CHECK: Verify state change after blockchain confirmation
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('blockchainStateCheck', {
            detail: { account, timestamp: Date.now() }
          }));
        }, 3000);

        return res;
      }
    } catch (err) {
      console.error("Cancel limit order error:", err);
      toast.error("Failed to cancel limit order");
    }
  };

  // getMarketName is now imported from marketUtils

  // Get trade index number
  const tradeIndexNumber = getTradeIndexNumber(trade.user_trade_index);

  // Format collateral amount with separate styling
  const collateralData = formatCollateralAmount(trade.collateral_amount);

  // Format size with separate styling
  const sizeData = formatSizeAmount(trade.collateral_amount, trade.leverage);

  // Format date with time - same as TradesRow
  const timestamp = trade.trade_info?.last_oi_update_ts || trade.open_timestamp;
  const dateTimeFormatted = timestamp ? formatDate(timestamp) : { date: "Recent", time: "" };

  // Get market name
  const market = getMarketName(trade.market_index);

  // Detect if this order is for a low-price token (e.g. PUMP, PENGU, BONK, SHIB, NIBI)
  const marketForOrder = getMarketByMarketIndex(trade.market_index);
  const isLowPriceToken =
    marketForOrder && lowPriceTokenIds.has(marketForOrder.id);

  const handleRowClick = (e) => {
    // Don't navigate if clicking on action buttons
    if (e.target.closest('button') || e.target.closest('.action-buttons')) {
      return;
    }
    if (onTradeClick) {
      onTradeClick(trade);
    }
  };

  const orderPrice = (() => {
    const num = parseFloat(trade.open_price);
    if (isLowPriceToken && !isNaN(num) && Math.abs(num) > 0 && Math.abs(num) < 1) {
      const raw = Math.abs(num).toFixed(20);
      const decimalPart = raw.split(".")[1] || "";
      let zeroCount = 0;
      for (let i = 0; i < decimalPart.length; i++) {
        if (decimalPart[i] === "0") zeroCount++;
        else break;
      }
      const remaining = decimalPart.slice(zeroCount).slice(0, 3);
      if (remaining) {
        return <>$0.0<sub>{zeroCount}</sub>{remaining}</>;
      }
    }
    return formatPriceWithSignificantDecimals(trade.open_price || 0, 8, true);
  })();

  return (
    <>
      {/* ─── Mobile: one order per card ─── */}
      <div
        onClick={handleRowClick}
        className="ce-tcard ce-tabular md:hidden"
        style={{ fontFamily: "General Sans" }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[15px] font-bold truncate" style={{ color: "var(--ink)" }}>
                {market.split('/')[0]}
              </span>
              <span className="text-[11px]" style={{ color: "var(--ink-4)" }}>
                /{market.split('/')[1]}
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-1.5">
              <span
                className="ce-chip"
                style={{
                  height: 20,
                  fontSize: 10,
                  background: trade.long ? "var(--tint-up)" : "var(--tint-down)",
                  color: trade.long ? "var(--on-tint-up)" : "var(--on-tint-down)",
                }}
              >
                {trade.long ? "BUY" : "SELL"}
              </span>
              <span className="text-[11px]" style={{ color: "var(--ink-3)" }}>
                {Number(trade.leverage).toFixed(1)}x · LIMIT
              </span>
              <span className="text-[11px]" style={{ color: "var(--ink-4)" }}>#{tradeIndexNumber}</span>
            </div>
          </div>
          <div className="text-right shrink-0">
            <span className="ce-tcard-k">Trigger</span>
            <span className="ce-figure text-[15px] font-bold" style={{ color: "var(--cat-accent)" }}>
              {orderPrice}
            </span>
          </div>
        </div>

        <div className="ce-tcard-grid" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
          <div>
            <span className="ce-tcard-k">Margin</span>
            <span className="ce-tcard-v">{collateralData.amount}</span>
          </div>
          <div>
            <span className="ce-tcard-k">Size</span>
            <span className="ce-tcard-v">{sizeData.amount}</span>
          </div>
          <div>
            <span className="ce-tcard-k">Placed</span>
            <span className="ce-tcard-v" style={{ fontSize: 12 }}>{dateTimeFormatted.date}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3">
          <div
            className="action-buttons flex-1"
            onClick={(e) => {
              e.stopPropagation();
              setModalShow(true);
            }}
          >
            <GreenButton text="Edit" className="w-full" />
          </div>
          <div
            className="action-buttons flex-1"
            onClick={(e) => {
              e.stopPropagation();
              handleCancelLimitOrder();
            }}
          >
            <RedButton text="Cancel" className="w-full" />
          </div>
        </div>
      </div>

      <div
        key={trade.user_trade_index}
        onClick={handleRowClick}
        className="ce-row-card ce-tabular hidden md:grid grid-cols-9 cursor-pointer text-sm min-w-[900px] whitespace-nowrap py-2 px-3 sm:px-5"
        style={{ fontFamily: "General Sans", minHeight: 46, marginBottom: 6 }}
      >
        {/* Trade Index */}
        <div className="flex items-center justify-center">
          <span className="text-sm font-medium" style={{ color: "var(--ink)" }}>{tradeIndexNumber}</span>
        </div>

        {/* Date & Time */}
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="text-xs" style={{ color: "var(--ink-3)" }}>{dateTimeFormatted.time}</div>
            <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>{dateTimeFormatted.date}</div>
          </div>
        </div>

        {/* Type */}
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center">
            <span
              className="text-sm font-semibold"
              style={{ color: trade.long ? 'var(--up)' : 'var(--down)' }}
            >
              {trade.long ? "BUY" : "SELL"}
            </span>
            <span className="text-xs" style={{ color: "var(--ink-3)" }}>LIMIT</span>
          </div>
        </div>

        {/* Pair */}
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="font-medium text-sm" style={{ color: "var(--ink)" }}>{market.split('/')[0]}</div>
            <div className="text-xs" style={{ color: "var(--ink-3)" }}>{market.split('/')[1]}</div>
          </div>
        </div>

        {/* Collateral Amount */}
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>{collateralData.amount}</div>
            <div className="text-xs" style={{ color: "var(--ink-3)" }}>{collateralData.currency}</div>
          </div>
        </div>

        {/* Size */}
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>{sizeData.amount}</div>
            <div className="text-xs" style={{ color: "var(--ink-3)" }}>{sizeData.currency}</div>
          </div>
        </div>

        {/* Leverage */}
        <div className="flex items-center justify-center text-sm font-medium" style={{ color: "var(--ink)" }}>
          {Number(trade.leverage).toFixed(1)}x
        </div>

        {/* Price */}
        <div className="flex items-center justify-center text-sm font-medium" style={{ color: "var(--ink)" }}>
          {orderPrice}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <div
              className="action-buttons w-[74px]"
            onClick={(e) => {
              e.stopPropagation();
              setModalShow(true);
            }}
          >
              <GreenButton text="Edit" className="w-full" />
            </div>

            <div
              className="action-buttons w-[74px]"
              onClick={(e) => {
                e.stopPropagation();
                handleCancelLimitOrder();
              }}
            >
              <RedButton text="Cancel" className="w-full" />
            </div>
          </div>
        </div>
      </div>
      <UpdateActionsModal
        isOpen={modalShow}
        setIsOpen={setModalShow}
        trade={trade}
      />
    </>
  );
};

export default OrdersRow;